/**
 * Speech Recognition Service Types
 */

/**
 * Result from speech recognition
 */
export interface TranscriptResult {
  /** The transcribed text */
  text: string;
  /** Whether this is a final result (sentence complete) or interim */
  isFinal: boolean;
  /** Confidence score from 0.0 to 1.0 (if available) */
  confidence?: number;
  /** Language code (e.g., 'el-GR' for Greek) */
  language: string;
  /** Timestamp when the result was generated */
  timestamp: number;
}

/**
 * Speech recognition error
 */
export interface SpeechRecognitionError {
  /** Error code */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Whether the error is recoverable */
  recoverable: boolean;
}

/**
 * Callback types
 */
export type OnResultCallback = (result: TranscriptResult) => void;
export type OnErrorCallback = (error: SpeechRecognitionError) => void;
export type OnStatusChangeCallback = (status: RecognitionStatus) => void;

/**
 * Recognition status
 */
export enum RecognitionStatus {
  IDLE = 'IDLE',
  STARTING = 'STARTING',
  LISTENING = 'LISTENING',
  STOPPING = 'STOPPING',
  ERROR = 'ERROR',
}

/**
 * Speech recognition service interface
 */
export interface ISpeechRecognitionService {
  /** Start speech recognition */
  start(): void;
  /** Stop speech recognition */
  stop(): void;
  /** Check if the service is supported in this browser */
  isSupported(): boolean;
  /** Check if currently listening */
  isListening(): boolean;
  /** Get current status */
  getStatus(): RecognitionStatus;
  /** Register result callback */
  onResult(callback: OnResultCallback): void;
  /** Register error callback */
  onError(callback: OnErrorCallback): void;
  /** Register status change callback */
  onStatusChange(callback: OnStatusChangeCallback): void;
  /** Update the recognition language (stops recognition if running) */
  setLanguage(language: string): void;
  /** Clean up resources */
  destroy(): void;
}

/**
 * Service configuration options
 */
export interface SpeechRecognitionConfig {
  /** Language code (default: 'el-GR') */
  language?: string;
  /** Enable continuous recognition (default: true) */
  continuous?: boolean;
  /** Return interim results (default: true) */
  interimResults?: boolean;
  /** Maximum alternatives per result (default: 1) */
  maxAlternatives?: number;
}

/**
 * Default configuration
 */
export const DEFAULT_CONFIG: Required<SpeechRecognitionConfig> = {
  language: 'el-GR',
  continuous: true,
  interimResults: true,
  maxAlternatives: 1,
};
